"use client"

import { useTranslations } from "next-intl"
import { AlertCircle, Check, Loader2 } from "lucide-react"
import { describeSaveStatus, type SaveStatus as SaveStatusValue } from "./core"

const TONE_CLS = {
  muted: "text-[var(--redy-muted)]",
  success: "text-[var(--redy-success)]",
  error: "text-[var(--redy-error)]",
} as const

export interface SaveStatusProps {
  status: SaveStatusValue
  error?: string | null
  className?: string
}

/**
 * Indicador inline por-campo. Reserva min-width en idle para no desplazar el
 * layout al aparecer un estado. aria-live anuncia el cambio a lectores.
 */
export function SaveStatus({ status, error = null, className = "" }: SaveStatusProps) {
  const t = useTranslations("common")
  const view = describeSaveStatus(status, error)

  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] min-w-[84px] ${view ? TONE_CLS[view.tone] : ""} ${className}`}
      aria-live="polite"
      role={view?.role}
    >
      {view?.kind === "saving" && (
        <>
          <Loader2 className="w-3 h-3 animate-spin" aria-hidden /> {t("saving")}
        </>
      )}
      {view?.kind === "saved" && (
        <>
          <Check className="w-3 h-3" aria-hidden /> {t("saved")}
        </>
      )}
      {view?.kind === "error" && (
        <>
          <AlertCircle className="w-3 h-3" aria-hidden /> {view.message || t("saveError")}
        </>
      )}
    </span>
  )
}
